import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useLayoutEffect, useRef, useState } from 'react';
// A control that morphs between a 50px icon puck and a full panel with the same
// two-stage motion as the Control Panel: hover widens the puck into a labelled
// pill, click grows the width leg first (into a rounded header bar) then the
// height leg (the drop-down), never a diagonal oval. Shares the forest-glass +
// pewter family. Content is measured at its final width so the height leg has a
// stable target, and kept mounted so the measurement stays live.
export const MorphPanel = ({ collapsed, onToggle, icon, label, anchor, expandedWidth, maxHeight = 'min(85vh, calc(100dvh - 6rem))', children, }) => {
    const [hovered, setHovered] = useState(false);
    const labelRef = useRef(null);
    const [labelWidth, setLabelWidth] = useState(120);
    const contentRef = useRef(null);
    const [contentHeight, setContentHeight] = useState(320);
    // top-right / bottom-right pin the icon to the right edge (label reveals left);
    // top-left pins it left (label reveals right).
    const iconRight = anchor !== 'top-left';
    useLayoutEffect(() => {
        if (labelRef.current)
            setLabelWidth(labelRef.current.scrollWidth);
    }, [label]);
    // Measure the content's natural height (it's laid out at the fixed expanded
    // width, so this is stable through the animation) and track it live.
    useLayoutEffect(() => {
        const el = contentRef.current;
        if (!el)
            return;
        const measure = () => setContentHeight(el.scrollHeight);
        measure();
        let ro;
        if (typeof ResizeObserver !== 'undefined') {
            ro = new ResizeObserver(measure);
            ro.observe(el);
        }
        return () => ro === null || ro === void 0 ? void 0 : ro.disconnect();
    }, []);
    const PUCK = 50;
    const width = collapsed ? (hovered ? PUCK + labelWidth : PUCK) : expandedWidth;
    // Open: width leg first, then the delayed height leg. Close/hover: no delay so
    // hover stays snappy and the panel un-drops before it narrows.
    const transition = collapsed
        ? 'width 240ms cubic-bezier(0.4,0,0.2,1), height 200ms cubic-bezier(0.4,0,0.2,1), border-radius 200ms ease-out'
        : 'width 240ms cubic-bezier(0.4,0,0.2,1), height 300ms cubic-bezier(0.4,0,0.2,1) 220ms, border-radius 160ms ease-out';
    return (_jsxs("div", { onMouseEnter: () => setHovered(true), onMouseLeave: () => setHovered(false), className: "relative overflow-hidden backdrop-blur-sm border shadow-lg text-white", style: {
            backgroundColor: 'var(--forest)',
            borderColor: 'var(--pewter)',
            width,
            height: collapsed ? PUCK : contentHeight,
            maxHeight: collapsed ? PUCK : maxHeight,
            borderRadius: collapsed ? 25 : 16,
            transition,
        }, children: [_jsx("div", { ref: contentRef, className: "absolute top-0 flex flex-col", style: {
                    width: expandedWidth,
                    [iconRight ? 'right' : 'left']: 0,
                    opacity: collapsed ? 0 : 1,
                    pointerEvents: collapsed ? 'none' : 'auto',
                    transition: collapsed ? 'opacity 90ms ease-out' : 'opacity 200ms ease-out 150ms',
                }, children: children }), _jsxs("button", { type: "button", onClick: onToggle, "aria-label": label, title: label, className: `absolute inset-0 flex items-center ${iconRight ? 'flex-row-reverse' : ''}`, style: {
                    opacity: collapsed ? 1 : 0,
                    pointerEvents: collapsed ? 'auto' : 'none',
                    transition: 'opacity 120ms ease-out',
                }, children: [_jsx("span", { className: "w-12 h-12 flex items-center justify-center shrink-0", children: icon }), _jsx("span", { ref: labelRef, className: `whitespace-nowrap overflow-hidden font-heading text-sm font-semibold ${iconRight ? 'pl-4' : 'pr-4'}`, style: {
                            color: 'var(--sand)',
                            maxWidth: collapsed && hovered ? labelWidth : 0,
                            opacity: collapsed && hovered ? 1 : 0,
                            transition: 'max-width 200ms ease-out, opacity 200ms ease-out',
                        }, children: label })] })] }));
};
export default MorphPanel;
